import { useId, useRef, useState } from 'react';
import { Download, Upload, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import ListRow from '../../ui/ListRow';
import Modal from '../../ui/Modal';
import Select from '../../ui/Select';
import Tag from '../../ui/Tag';
import { clean } from '../../shell/copy';
import { csvToScenes, downloadTemplate } from './scriptHelpers';

// The Scripts list's dialogs (create flows only, DESIGN §2.5): New script, Save as template, New script from a template,
// Reassign to another client. Same calls and payloads as before; the callers own the API work.

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
export const formatName = f => (f === 'TH' ? 'Talking head' : f || '');

// ── New script ────────────────────────────────────────────────────────────────
// onCreate(clientId, title, format, rowCount, csvScenes, writerId), as before.
export function NewScriptModal({ clients, members, onClose, onCreate, defaultClientId = '' }) {
  const { user } = useAuth();
  const formId = useId();
  const fileRef = useRef(null);
  const [clientId,   setClientId]   = useState(defaultClientId);
  const [title,      setTitle]      = useState('');
  const [format,     setFormat]     = useState('');
  const [rowCount,   setRowCount]   = useState(10);
  const [writerId,   setWriterId]   = useState('');
  const [csvScenes,  setCsvScenes]  = useState(null);
  const [csvName,    setCsvName]    = useState('');
  const [csvError,   setCsvError]   = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isAdmin = user?.roles?.includes('Admin') || user?.roles?.includes('Power User');
  const writerMembers = (members || []).filter(m => m.isActive !== false && m.roles?.some(r => r.role === 'Writer' || r.role === 'Power User'));
  const selectedClient = clients.find(c => c.id === clientId);
  const formats = (selectedClient?.allowedScriptFormats || []).filter(f => f !== 'None');
  const clearCsv = () => { setCsvScenes(null); setCsvName(''); setCsvError(''); };

  const handleCsvFile = e => {
    const file = e.target.files[0];
    if (!file || !format) return;
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = ev => {
      const scenes = csvToScenes(ev.target.result, format);
      if (scenes.length === 0) { setCsvError(`${file.name} has no rows this format can read. Its headers must match the downloaded template.`); return; }
      setCsvError('');
      setCsvScenes(scenes);
      setCsvName(file.name);
    };
    reader.readAsText(file);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (submitting || !clientId || !title || !format) return;
    setSubmitting(true);
    await onCreate(clientId, title, format || null, format ? Math.max(1, Math.min(400, rowCount)) : 0, csvScenes || undefined, writerId || undefined);
    setSubmitting(false);
  };

  return (
    <Modal title="New script" meta="It starts in progress, with you as the writer unless you assign one." onClose={onClose} testId="new-script-modal"
      footer={(
        <>
          <Button variant="quiet" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form={formId} data-testid="new-script-create" loading={submitting}
            disabled={!clientId || !title || !format} title={!format && clientId ? 'Choose a format first' : undefined}>Create script</Button>
        </>
      )}>
      <form id={formId} className="ui-stack" onSubmit={handleSubmit}>
        <Select label="Client" required value={clientId}
          onChange={e => { setClientId(e.target.value); setFormat(''); clearCsv(); }}
          options={[{ value: '', label: 'Choose a client' },
            ...clients.filter(c => (c.allowedScriptFormats || []).some(f => f !== 'None')).map(c => ({ value: c.id, label: clean(c.name) }))]} />
        <Input label="Title" required data-autofocus placeholder="For example, Reach Around" value={title} onChange={e => setTitle(e.target.value)} />
        {formats.length > 0 && (
          <Select label="Format" value={format} onChange={e => { setFormat(e.target.value); clearCsv(); }}
            options={[{ value: '', label: 'Choose a format' }, ...formats.map(f => ({ value: f, label: formatName(f) }))]} />
        )}
        {isAdmin && writerMembers.length > 0 && (
          <Select label="Writer" value={writerId} onChange={e => setWriterId(e.target.value)}
            options={[{ value: '', label: 'Assign to me' }, ...writerMembers.map(m => ({ value: m.id, label: clean(m.name) }))]} />
        )}
        {format && (
          <>
            {!csvScenes ? (
              <Input label="Starting rows" type="number" min={1} max={400} className="ui-fit--narrow" hint="1 to 400 blank rows"
                value={rowCount} onChange={e => setRowCount(Math.max(1, Math.min(400, parseInt(e.target.value, 10) || 1)))} />
            ) : (
              <div className="ui-cluster">
                <span className="ui-dense">{plural(csvScenes.length, 'row')} from {csvName}</span>
                <Button variant="quiet" icon={X} onClick={clearCsv}>Clear</Button>
              </div>
            )}
            <div className="ui-cluster">
              <Button variant="secondary" icon={Upload} onClick={() => fileRef.current?.click()}>Import from CSV</Button>
              <Button variant="quiet" icon={Download} onClick={() => downloadTemplate(format)}>Download template</Button>
              <input ref={fileRef} type="file" accept=".csv" hidden onChange={handleCsvFile} />
            </div>
            {csvError && <p className="ui-p ui-dense ui-text-danger" role="alert">{csvError}</p>}
          </>
        )}
      </form>
    </Modal>
  );
}

// ── Save as template ──────────────────────────────────────────────────────────
// The name defaults to the script's; blank falls back to it (server-side too).
export function SaveTemplateModal({ defaultName, onClose, onSave }) {
  const formId = useId();
  const [name, setName] = useState(defaultName || '');
  const [busy, setBusy] = useState(false);
  const submit = e => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    onSave(name.trim() || defaultName || '');
  };
  return (
    <Modal title="Save as template" onClose={onClose} testId="save-template-modal"
      footer={(
        <>
          <Button variant="quiet" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form={formId} loading={busy}>Save template</Button>
        </>
      )}>
      <form id={formId} className="ui-stack" onSubmit={submit}>
        <Input label="Template name" data-autofocus value={name} placeholder={defaultName} onChange={e => setName(e.target.value)} />
        <p className="ui-p ui-dense ui-text-2">
          The template copies this script's format and content; the script itself does not change. Templates never show in
          script lists, counts, reports or production.
        </p>
      </form>
    </Modal>
  );
}

// ── New script from a template ────────────────────────────────────────────────
// Only clients that allow the template's format are offered (the server refuses the rest).
export function UseTemplateModal({ template, clients, onClose, onCreate }) {
  const formId = useId();
  const eligible = (clients || []).filter(c => !template.format || (c.allowedScriptFormats || []).includes(template.format));
  const [clientId, setClientId] = useState(() => (eligible.some(c => c.id === template.client?.id) ? template.client.id : (eligible[0]?.id || '')));
  const [busy, setBusy] = useState(false);
  const submit = e => {
    e.preventDefault();
    if (busy || !clientId) return;
    setBusy(true);
    onCreate(clientId);
  };
  const name = clean(template.templateName || template.scriptName);
  return (
    <Modal title={`New script from ${name}`} onClose={onClose} testId="use-template-modal"
      footer={(
        <>
          <Button variant="quiet" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form={formId} loading={busy} disabled={!clientId}>Create script</Button>
        </>
      )}>
      <form id={formId} className="ui-stack" onSubmit={submit}>
        <Select label="Client" value={clientId} onChange={e => setClientId(e.target.value)}
          options={eligible.map(c => ({ value: c.id, label: clean(c.name) }))} />
        {eligible.length === 0 && (
          <p className="ui-p ui-dense ui-text-danger" role="alert">No client takes the {formatName(template.format)} format, so this template cannot be used yet.</p>
        )}
        <p className="ui-p ui-dense ui-text-2">
          Creates an in-progress script in the {template.format ? `${formatName(template.format)} format` : 'template’s format'}, with you as the writer.
        </p>
      </form>
    </Modal>
  );
}

// ── Reassign to another client ────────────────────────────────────────────────
// data: what the server allows for the selection ({ movable, blocked, targets }); result: what the move did. The target
// list is the server's and is not re-filtered here (a client that cannot take the selection is simply absent).
export function ReassignModal({ data, result, busy, onClose, onPick }) {
  const { movable = [], blocked = [], targets = [] } = data || {};
  const fromInitials = [...new Set(movable.map(m => m.clientInitials).filter(Boolean))];
  const one = movable.length === 1;
  const to = clean(result?.toProject?.name);
  const title = result ? `Moved to ${to}` : movable.length === 0 ? 'Nothing to move' : `Move ${plural(movable.length, 'script')} to another client`;
  return (
    <Modal title={title} onClose={busy ? undefined : onClose} testId="reassign-modal"
      footer={<Button variant={result ? 'primary' : 'quiet'} disabled={busy} onClick={onClose}>{result ? 'Done' : 'Cancel'}</Button>}>
      {result ? (
        <div className="ui-stack">
          {(result.reassigned || []).length === 0 ? (
            <p className="ui-p ui-dense ui-text-2">No script was moved.</p>
          ) : (
            <ul className="ui-plainlist ui-stack">
              {result.reassigned.map(r => (
                <li key={r.id} className="ui-stack ui-stack--tight">
                  <span className="ui-meta">Was {clean(r.oldScriptName)}</span>
                  <span className="ui-dense">{clean(r.scriptName)}</span>
                </li>
              ))}
            </ul>
          )}
          {result.segmentsMoved > 0 && (
            <p className="ui-p ui-dense ui-text-2">
              {plural(result.segmentsMoved, 'scheduled shoot segment')} moved to {to} on the same {result.segmentsMoved === 1 ? 'day' : 'days'}, with the same talent.
            </p>
          )}
          {result.slotsCleared > 0 && (
            <p className="ui-p ui-dense ui-text-2">
              {plural(result.slotsCleared, 'posting-schedule slot')} {result.slotsCleared === 1 ? 'was' : 'were'} cleared. A slot is ordered within one
              client, so {to} needs to schedule {result.reassigned?.length === 1 ? 'this video' : 'these videos'} itself.
            </p>
          )}
          {(result.skipped || []).length > 0 && (
            <section className="ui-section" aria-label="Skipped">
              <h3 className="ui-heading ui-text-danger">{result.skipped.length} skipped</h3>
              <ul className="ui-plainlist ui-stack ui-stack--tight">
                {result.skipped.map(s => <li key={s.id} className="ui-dense ui-text-2">{clean(s.scriptName || s.id)}: {clean(s.reason)}</li>)}
              </ul>
            </section>
          )}
        </div>
      ) : (
        <div className="ui-stack">
          {blocked.length > 0 && (
            <section className="ui-region ui-region--pad ui-stack ui-stack--tight" aria-label="Cannot be reassigned">
              <h3 className="ui-heading ui-text-danger">{blocked.length} of the selected {blocked.length === 1 ? 'script cannot' : 'scripts cannot'} be reassigned</h3>
              <ul className="ui-plainlist ui-stack ui-stack--tight">
                {blocked.map(b => <li key={b.id} className="ui-dense ui-text-2">{clean(b.scriptName || b.id)}: {clean(b.reason)}</li>)}
              </ul>
              <p className="ui-p ui-meta">Only a script that has not moved into filming can change client.</p>
            </section>
          )}
          {movable.length === 0 ? (
            <p className="ui-p ui-dense ui-text-2">None of the selected scripts can be reassigned.</p>
          ) : targets.length === 0 ? (
            <p className="ui-p ui-dense ui-text-2">
              No other client is set up to take {one ? `the ${formatName(movable[0].format) || 'formatless'} format` : `all ${movable.length} of these formats`}.
              {!one && ' Try reassigning them one format at a time.'} An Admin can add the format to a client on the Admin page, Projects tab.
            </p>
          ) : (
            <div className="ui-list" role="list" aria-label="Clients that can take the selection">
              {targets.map(t => (
                <ListRow key={t.id} title={clean(t.name)} onClick={busy ? undefined : () => onPick(t.id)}
                  trailing={<Tag>{fromInitials.length === 1 ? `${fromInitials[0]}- to ${t.initials}-` : `${t.initials}-`}</Tag>} />
              ))}
            </div>
          )}
          {targets.length > 0 && (
            <p className="ui-p ui-meta">
              {one ? 'The script is' : 'Each script is'} renamed onto the receiving client's initials
              {fromInitials.length === 1 ? ` (${fromInitials[0]}- becomes the new client's)` : ''}, and {one ? 'its' : 'their'} production card,
              shoot days and casting move with {one ? 'it' : 'them'}.
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
